// src/pages/bloomterminal/BiQ.jsx - Bloom Intelligence chat interface (formerly dashboard)
import React, { useState, useRef, useEffect, useMemo, useCallback, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Download, Share2, Copy, Pencil, Check, CirclePlay, ArrowRight, Loader2, AlertTriangle, X } from 'lucide-react';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area, PieChart, Pie,
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell, ZAxis
} from 'recharts';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { CARTO_DARK_URL } from '../constants/URLs';
import {
  downloadPdfReport,
  finalizeBiqAssistantContent,
  getLatestPdfReport,
  sendBiqAgenticMessage,
  sendBiqResponseFeedback,
  uploadBiqUserFile,
} from '../utils/api';
import { findBiqChatIdForUserMessage } from '../utils/biqChatHelpers';
import { createLeafletPinIcon } from './components/map/leafletPinIcon';
import BiqComposer, { BiqAttachedFileChip } from '../components/BiqComposer';
import { useBiqPageContext } from '../context/BiqPageContext';

const PlotlyChart = React.lazy(() => import('./components/charts/PlotlyChart'));
import ChartErrorBoundary from './components/charts/ChartErrorBoundary';

import { useUser } from '../context/UserContext';
import { useGlobalFontSize } from '../context/GlobalFontSizeContext';
import { useTheme } from '../context/ThemeContext';
import { useBiqChatHistory } from '../context/BiqChatHistoryContext';
import { useBiqLiveChats, useLiveChat } from '../context/BiqLiveChatsContext';
import { MAX_CONCURRENT_TURNS, isBusy } from '../context/biqLiveChatsStore';
import { useTutorial } from '../context/TutorialContext';
import DataReportsDialog from './components/DataReportsDialog';
import ShareModal from './components/ShareModal';
import BiqResponseActions from './components/BiqResponseActions';
import BiqLongPressMenu from './components/BiqLongPressMenu';
import { useHasHover } from './hooks/useMediaQuery';
import { trackEvent, trackChatMessageSent, EVENTS } from '../utils/analytics';
import { getSmartYDomain } from './utils/analyticsHelpers';
import { useUsageTracking } from '../hooks/useUsageTracking';
import { USAGE_FEATURES } from '../constants/UsageLimits';
import UsageBlurOverlay from '../components/UsageBlurOverlay';
import UsageGuard from '../components/UsageGuard';
import { PIN_HOVER_CSS } from '../utils/pinHoverCss.js';

// Long-press on a sent question (touch only) opens the action menu.
const LONG_PRESS_MS = 500;
const LONG_PRESS_SLOP_PX = 10;

/** Streaming status under BiQ answer: omit error-like tool messages; generic record counts. */
function biqStatusLineLooksLikeError(s) {
  const lower = s.toLowerCase();
  if (/\btraceback\b/.test(lower)) return true;
  if (/\berror\s*:/.test(lower)) return true;
  if (/\bexception\b/.test(lower)) return true;
  if (/\bfailed\b/.test(lower)) return true;
  if (/\bfailure\b/.test(lower)) return true;
  if (/\bsyntax error\b/.test(lower)) return true;
  if (/\b(timeout|timed out)\b/.test(lower)) return true;
  if (/\b(unauthorized|forbidden|not found)\b/.test(lower)) return true;
  if (/\b(status\s*)?(4\d{2}|5\d{2})\b/.test(lower)) return true;
  if (/^\s*at\s+[\w.$[\]]+\s*\(/m.test(s)) return true;
  return false;
}

function biqStatusLineStripRecordCount(s) {
  return s
    .replace(/\bfound\s+\d{1,9}\s+records?\b/gi, 'Found records')
    .replace(/\b\d{1,9}\s+records?\s+found\b/gi, 'Found records');
}

function formatBiqStatusLineForDisplay(raw) {
  if (raw == null || typeof raw !== 'string') return '';
  const s = raw.trim();
  if (!s) return '';
  if (biqStatusLineLooksLikeError(s)) return '';
  return biqStatusLineStripRecordCount(s);
}

// Custom Leaflet popup styles for dark theme (injected via style tag in MapChart)
const leafletCustomStyles = `
  .leaflet-popup-content-wrapper {
    background-color: #1d1d1d;
    color: #f0f5f2;
    --harvest-not-producing-text: #9CA3AF;
    border-radius: 8px;
    border: 1px solid #333;
  }
  .leaflet-popup-tip {
    background-color: #1d1d1d;
    border: 1px solid #333;
  }
  .leaflet-popup-close-button {
    color: #888 !important;
  }
  .leaflet-popup-close-button:hover {
    color: var(--bloom-green) !important;
  }
  .leaflet-container {
    background-color: #1d1d1d;
  }
  .leaflet-control-attribution {
    background-color: rgba(29, 29, 29, 0.8) !important;
    color: #666 !important;
  }
  .leaflet-control-attribution a {
    color: var(--bloom-green) !important;
  }
  .leaflet-control-zoom a {
    background-color: #282a29 !important;
    color: #f0f5f2 !important;
    border-color: #333 !important;
  }
  .leaflet-control-zoom a:hover {
    background-color: var(--bloom-green) !important;
    color: #1d1d1d !important;
  }
  .leaflet-pin-icon {
    background: transparent !important;
    border: none !important;
  }
  ${PIN_HOVER_CSS}
`;

// Chart colors for multiple series
const CHART_COLORS = ['#16A34A', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

// Shared horizontal page margin, matching the other dashboard pages (Analytics, Alerts).
const PAGE_X = 'px-[16px] lg:px-[32px]';

// INITCAP helper - capitalize first letter of each word
const toInitCap = (str) => {
  if (!str || typeof str !== 'string') return '';
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Component to fit map bounds to markers (with responsive resize handling)
function FitBounds({ markers }) {
  const map = useMap();

  // Invalidate size on mount to ensure Leaflet recalculates for responsive container
  useEffect(() => {
    const timer = setTimeout(() => map.invalidateSize(), 100);
    return () => clearTimeout(timer);
  }, [map]);

  // Handle window resize - recalculate bounds
  useEffect(() => {
    const handleResize = () => {
      map.invalidateSize();
      // Refit bounds after resize
      if (markers && markers.length > 0) {
        const validMarkers = markers.filter(m =>
          m.lat !== undefined && m.lng !== undefined &&
          !isNaN(m.lat) && !isNaN(m.lng)
        );
        if (validMarkers.length > 0) {
          const bounds = L.latLngBounds(validMarkers.map(m => [m.lat, m.lng]));
          map.fitBounds(bounds, { padding: [60, 60], maxZoom: 5, animate: false });
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [map, markers]);

  // Fit bounds when markers change
  useEffect(() => {
    if (markers && markers.length > 0) {
      const validMarkers = markers.filter(m =>
        m.lat !== undefined && m.lng !== undefined &&
        !isNaN(m.lat) && !isNaN(m.lng)
      );

      if (validMarkers.length > 0) {
        const bounds = L.latLngBounds(
          validMarkers.map(m => [m.lat, m.lng])
        );
        map.fitBounds(bounds, { padding: [60, 60], maxZoom: 5, animate: false });
      }
    }
  }, [markers, map]);

  return null;
}

// =============================================================================
// MAP CHART CONFIGURATION
// =============================================================================

// Country/Region coordinates database (same as Flask app)
const COUNTRY_COORDS = {
  // Americas
  'usa': [37.0902, -95.7129],
  'united states': [37.0902, -95.7129],
  'mexico': [23.6345, -102.5528],
  'chile': [-35.6751, -71.5430],
  'peru': [-9.19, -75.0152],
  'brazil': [-14.235, -51.9253],
  'argentina': [-38.4161, -63.6167],
  'colombia': [4.5709, -74.2973],
  'ecuador': [-1.8312, -78.1834],
  'guatemala': [15.7835, -90.2308],
  'costa rica': [9.7489, -83.7534],
  'honduras': [15.2, -86.2419],
  // US States/Regions
  'california': [36.7783, -119.4179],
  'florida': [27.6648, -81.5158],
  'central florida': [28.4814, -81.5089],
  'south florida': [25.7617, -80.1918],
  'texas': [31.9686, -99.9018],
  'arizona': [34.0489, -111.0937],
  'washington': [47.7511, -120.7401],
  'oregon': [43.8041, -120.5542],
  'michigan': [44.3148, -85.6024],
  'new york': [40.7128, -74.0060],
  'georgia': [32.1656, -82.9001],
  // Africa
  'kenya': [-0.0236, 37.9062],
  'ethiopia': [9.145, 40.4897],
  'south africa': [-30.5595, 22.9375],
  'tanzania': [-6.369, 34.8888],
  'uganda': [1.3733, 32.2903],
  'rwanda': [-1.9403, 29.8739],
  'nigeria': [9.082, 8.6753],
  'ghana': [7.9465, -1.0232],
  'ivory coast': [7.54, -5.5471],
  'cameroon': [7.3697, 12.3547],
  'egypt': [26.8206, 30.8025],
  'morocco': [31.7917, -7.0926],
  // Europe
  'spain': [40.4637, -3.7492],
  'italy': [41.8719, 12.5674],
  'france': [46.2276, 2.2137],
  'netherlands': [52.1326, 5.2913],
  'germany': [51.1657, 10.4515],
  'poland': [51.9194, 19.1451],
  'greece': [39.0742, 21.8243],
  'turkey': [38.9637, 35.2433],
  // Asia
  'china': [35.8617, 104.1954],
  'india': [20.5937, 78.9629],
  'vietnam': [14.0583, 108.2772],
  'thailand': [15.87, 100.9925],
  'indonesia': [-0.7893, 113.9213],
  'philippines': [12.8797, 121.774],
  'japan': [36.2048, 138.2529],
  // Oceania
  'australia': [-25.2744, 133.7751],
  'new zealand': [-40.9006, 174.886],
};

// Status colors for map markers (same as Flask app)
const STATUS_COLORS = {
  producing: '#16A34A',       // Green
  not_producing: '#6B7280',   // Grey
  starting_soon: '#3b82f6',   // Blue
  ending_soon: '#f97316',     // Orange
};

// Status label mapping for display
const STATUS_LABELS = {
  producing: 'Currently Producing',
  not_producing: 'Not Producing',
  starting_soon: 'Starting Soon',
  ending_soon: 'Ending Soon',
};

// Normalize status strings to standard format
const normalizeStatus = (status) => {
  if (!status) return 'producing';
  // Convert underscores to spaces for consistent lookup
  const normalized = status.toLowerCase().trim().replace(/_/g, ' ');
  const STATUS_MAP = {
    'currently harvesting': 'producing',
    'harvesting': 'producing',
    'in season': 'producing',
    'active': 'producing',
    'producing': 'producing',
    'pre-harvest': 'starting_soon',
    'starting soon': 'starting_soon',
    'off season': 'not_producing',
    'not producing': 'not_producing',
    'ending': 'ending_soon',
    'ending soon': 'ending_soon',
  };
  return STATUS_MAP[normalized] || 'producing';
};

// Get coordinates for a region (with fuzzy matching)
const getCoordinates = (region) => {
  if (!region) return null;
  const key = region.toLowerCase().trim();

  // Exact match
  if (COUNTRY_COORDS[key]) return COUNTRY_COORDS[key];

  // Partial match - search for substring
  for (const [coordKey, coords] of Object.entries(COUNTRY_COORDS)) {
    if (key.includes(coordKey) || coordKey.includes(key)) {
      return coords;
    }
  }

  return null;
};

// Custom markdown styles for BiQ responses
const markdownComponents = {
  h1: ({ node, ...props }) => <h1 className="text-2xl font-bold text-bloom-green mb-4 mt-2" {...props} />,
  h2: ({ node, ...props }) => <h2 className="text-xl font-semibold text-bloom-green mb-3 mt-4" {...props} />,
  h3: ({ node, ...props }) => <h3 className="text-lg font-semibold text-bloom-green mb-2 mt-3" {...props} />,
  h4: ({ node, ...props }) => <h4 className="text-base font-semibold text-bloom-green mb-2 mt-2" {...props} />,
  p: ({ node, ...props }) => <p className="mb-3 leading-relaxed text-[#282A29] dark:text-[#f0f5f2]" {...props} />,
  ul: ({ node, ...props }) => <ul className="list-disc pl-5 mb-3 space-y-1" {...props} />,
  ol: ({ node, ...props }) => <ol className="list-decimal pl-5 mb-3 space-y-1" {...props} />,
  li: ({ node, ...props }) => <li className="text-[#282A29] dark:text-[#f0f5f2]" {...props} />,
  strong: ({ node, ...props }) => <strong className="font-bold text-bloom-green" {...props} />,
  em: ({ node, ...props }) => <em className="italic text-[#828382] dark:text-[#d0d5d2]" {...props} />,
  code: ({ node, inline, ...props }) => {
    const isDark = document.documentElement.classList.contains('dark');
    return inline ? (
      <code className={`${isDark ? 'bg-[#1d1d1d]' : 'bg-[#F0F5F2]'} px-1.5 py-0.5 rounded text-bloom-green text-sm font-mono`} {...props} />
    ) : (
      <code className={`block ${isDark ? 'bg-[#1d1d1d]' : 'bg-[#F0F5F2]'} p-3 rounded-lg text-sm font-mono overflow-x-auto mb-3 text-[#282A29] dark:text-[#f0f5f2]`} {...props} />
    );
  },
  pre: ({ node, ...props }) => {
    const isDark = document.documentElement.classList.contains('dark');
    return <pre className={`${isDark ? 'bg-[#1d1d1d]' : 'bg-[#F0F5F2]'} p-3 rounded-lg overflow-x-auto mb-3`} {...props} />;
  },
  blockquote: ({ node, ...props }) => (
    <blockquote className="border-l-4 border-bloom-green pl-4 py-1 my-3 text-[#828382] dark:text-[#d0d5d2] italic" {...props} />
  ),
  a: ({ node, ...props }) => (
    <a className="text-bloom-green underline hover:text-bloom-green-hover transition-colors" target="_blank" rel="noopener noreferrer" {...props} />
  ),
  table: ({ node, ...props }) => {
    const isDark = document.documentElement.classList.contains('dark');
    return (
      <div className="overflow-x-auto mb-3">
        <table className={`min-w-full border-collapse border ${isDark ? 'border-[#3d3d3d]' : 'border-[#E8EBE9]'}`} {...props} />
      </div>
    );
  },
  thead: ({ node, ...props }) => {
    const isDark = document.documentElement.classList.contains('dark');
    return <thead className={isDark ? 'bg-[#1d1d1d]' : 'bg-[#F0F5F2]'} {...props} />;
  },
  th: ({ node, ...props }) => {
    const isDark = document.documentElement.classList.contains('dark');
    return <th className={`border ${isDark ? 'border-[#3d3d3d]' : 'border-[#E8EBE9]'} px-4 py-3 text-left text-bloom-green font-semibold`} {...props} />;
  },
  td: ({ node, ...props }) => {
    const isDark = document.documentElement.classList.contains('dark');
    return <td className={`border ${isDark ? 'border-[#3d3d3d]' : 'border-[#E8EBE9]'} px-4 py-3 text-[#282A29] dark:text-[#f0f5f2]`} {...props} />;
  },
  hr: ({ node, ...props }) => {
    const isDark = document.documentElement.classList.contains('dark');
    return <hr className={`${isDark ? 'border-[#3d3d3d]' : 'border-[#E8EBE9]'} my-4`} {...props} />;
  },
};

// =============================================================================
// MAP CHART COMPONENT (Enhanced with grouped data support)
// =============================================================================
const MapChart = ({ chartData }) => {
  const { fontSize } = useGlobalFontSize();
  const { title, data, config } = chartData;
  const regionField = config?.region_field || 'region';
  const statusField = config?.status_field || 'status';
  const description = config?.description;

  // Inject custom Leaflet styles on mount
  useEffect(() => {
    const styleId = 'leaflet-custom-styles';
    if (!document.getElementById(styleId)) {
      const styleEl = document.createElement('style');
      styleEl.id = styleId;
      styleEl.textContent = leafletCustomStyles;
      document.head.appendChild(styleEl);
    }
  }, []);

  // Process map markers with grouped data support
  const markers = useMemo(() => {
    return data
      .map((item) => {
        // Get coordinates - prefer explicit lat/lng, fallback to region lookup
        const lat = item.lat ?? item.latitude ?? item.Latitude;
        const lng = item.lng ?? item.lon ?? item.longitude ?? item.Longitude;

        let coords;
        if (lat !== undefined && lng !== undefined && !isNaN(lat) && !isNaN(lng)) {
          coords = [parseFloat(lat), parseFloat(lng)];
        } else {
          const region = item[regionField] || item.country || item.location || item.region;
          coords = getCoordinates(region);
        }
        if (!coords) return null;

        // Check if this is grouped data
        const isGrouped = Array.isArray(item.locations) ||
          (item.region && !item.specific_location && !item.location);

        // Extract status with fallback to parsing from label
        let status = item[statusField] || item.status || item.harvest_status;

        // Fallback: Extract status from label if missing
        if (!status && item.label && typeof item.label === 'string') {
          const labelParts = item.label.split(' - ');
          if (labelParts.length >= 2) {
            const extractedStatus = labelParts[labelParts.length - 1].trim().toLowerCase();
            if (['producing', 'not producing', 'starting soon', 'ending soon'].includes(extractedStatus)) {
              status = extractedStatus.replace(' ', '_');
            }
          }
        }

        status = normalizeStatus(status || 'producing');

        // Build display name
        let displayName;
        if (isGrouped) {
          const region = item.region ?? '';
          const country = item.country ?? '';
          displayName = [region, country]
            .map(p => toInitCap(p))
            .filter(p => p && p.trim())
            .join(', ') || item.label || 'Unknown';
        } else {
          const specificLocation = item.specific_location ?? item.location ?? '';
          const region = item.region ?? item.district ?? '';
          const country = item.country ?? item.Country ?? '';
          // Deduplicate: if specific_location equals region, only show once
          const parts = [specificLocation, region, country]
            .map(p => toInitCap(p))
            .filter(p => p && p.trim());
          // Remove consecutive duplicates (e.g., "North Carolina, North Carolina" -> "North Carolina")
          const deduped = parts.filter((p, i) => i === 0 || p.toLowerCase() !== parts[i-1].toLowerCase());
          displayName = deduped.join(', ') || item.label || 'Unknown';
        }

        return {
          lat: coords[0],
          lng: coords[1],
          region: item.region,
          label: displayName,
          status,
          color: STATUS_COLORS[status] || STATUS_COLORS.producing,
          isGrouped,
          locations: isGrouped ? item.locations : null,
          statusCounts: isGrouped ? item.status_counts : null,
          data: item,
        };
      })
      .filter(Boolean);
  }, [data, regionField, statusField]);

  // Get unique statuses for legend
  const uniqueStatuses = [...new Set(markers.map((m) => m.status))];
  const pinIconCache = useMemo(() => new Map(), []);
  const getPinIcon = useMemo(() => {
    return (fillColor, isGrouped, status) => {
      const sizePx = isGrouped ? 34 : 28;
      const variant = status === 'not_producing' ? 'glass' : 'solid';
      const key = `pin|${fillColor}|${sizePx}|${variant}`;
      const cached = pinIconCache.get(key);
      if (cached) return cached;
      const icon = createLeafletPinIcon({ fillColor, sizePx, variant });
      pinIconCache.set(key, icon);
      return icon;
    };
  }, [pinIconCache]);

  if (markers.length === 0) {
    return (
      <div className="mt-4 p-4 bg-white dark:bg-[#1d1d1d] rounded-lg">
        {title && <h3 className="text-bloom-green font-semibold mb-1" style={{ fontSize: fontSize + 2 }}>{title}</h3>}
        <div className="flex items-center justify-center text-[#828382] dark:text-[#888]" style={{ height: 'clamp(280px, 45vh, 450px)', fontSize: fontSize }}>
          No location data available for map
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 p-4 bg-white dark:bg-[#1d1d1d] rounded-lg">
      {/* Title and subtitle */}
      {title && <h3 className="text-bloom-green font-semibold mb-1 text-base">{title}</h3>}
      {description && <p className="text-[#828382] dark:text-[#888] text-sm mb-3">{description}</p>}

      {/* Map container */}
      <div className="rounded-lg overflow-hidden" style={{ height: 'clamp(280px, 45vh, 450px)', minHeight: '280px' }}>
        <MapContainer
          center={[20, 0]}
          zoom={2}
          style={{ height: '100%', width: '100%', background: '#1d1d1d' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            url={CARTO_DARK_URL}
            attribution='&copy; <a href="https://carto.com/">CARTO</a>'
          />
          <FitBounds markers={markers} />
          {markers.map((marker, index) => (
            <Marker
              key={`${marker.region || marker.label}-${index}`}
              position={[marker.lat, marker.lng]}
              icon={getPinIcon(marker.color, marker.isGrouped, marker.status)}
            >
              <Popup>
                <div className="text-sm" style={{ maxWidth: '250px' }}>
                  <strong className="text-bloom-green">{marker.label}</strong>
                  <br />
                  <span style={{ color: marker.status === 'not_producing' ? 'var(--harvest-not-producing-text, #9CA3AF)' : marker.color, fontWeight: 500 }}>
                    Status: {STATUS_LABELS[marker.status] || marker.status.replace(/_/g, ' ')}
                  </span>

                  {/* Grouped marker: show location list */}
                  {marker.isGrouped && marker.locations && marker.locations.length > 0 && (
                    <div style={{ marginTop: '8px', maxHeight: '150px', overflowY: 'auto' }}>
                      <strong>Locations ({marker.locations.length}):</strong>
                      <ul style={{ margin: '4px 0', paddingLeft: '16px', fontSize: fontSize - 3 }}>
                        {marker.locations.slice(0, 10).map((loc, i) => {
                          const isString = typeof loc === 'string';
                          const name = isString ? loc : (loc.name || 'Unknown');
                          const locStatus = isString ? null : loc.status;
                          const locColor = locStatus
                            ? (STATUS_COLORS[locStatus] || marker.color)
                            : marker.color;
                          return (
                            <li key={i} style={{ color: locColor === STATUS_COLORS.not_producing ? 'var(--harvest-not-producing-text, #9CA3AF)' : locColor }}>
                              {toInitCap(name)}{locStatus ? ` (${locStatus.replace(/_/g, ' ')})` : ''}
                            </li>
                          );
                        })}
                        {marker.locations.length > 10 && (
                          <li style={{ color: '#888', fontStyle: 'italic' }}>
                            ...and {marker.locations.length - 10} more
                          </li>
                        )}
                      </ul>
                    </div>
                  )}

                  {/* Individual marker: show commodity/harvest info */}
                  {!marker.isGrouped && marker.data && (
                    <>
                      {marker.data.commodity && (
                        <>
                          <br />
                          Commodity: {toInitCap(marker.data.commodity)}
                        </>
                      )}
                      {marker.data.harvest_months && (
                        <>
                          <br />
                          Harvest: {marker.data.harvest_months}
                        </>
                      )}
                    </>
                  )}
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 mt-3 justify-center">
        {uniqueStatuses.map((status) => (
          <div key={status} className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: STATUS_COLORS[status], opacity: status === 'not_producing' ? 0.55 : 1 }}
            />
            <span 
              className="text-[#282A29] dark:text-[#f0f5f2]"
              style={{ fontSize: fontSize - 2 }}
            >
              {STATUS_LABELS[status] || status.replace(/_/g, ' ')}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// =============================================================================
// CHART FILTER COMPONENT
// =============================================================================
const ChartFilters = ({ filters, activeFilters, onFilterChange }) => {
  if (!filters || Object.keys(filters).length === 0) return null;

  return (
    <div className="flex flex-wrap gap-3 mb-3">
      {Object.entries(filters).map(([field, filterConfig]) => (
        <div key={field} className="flex items-center gap-2">
          <label className="text-[#828382] dark:text-[#888] text-xs">{filterConfig.label}:</label>
          <select
            value={activeFilters[field] || 'All'}
            onChange={(e) => onFilterChange(field, e.target.value)}
            className="bg-white dark:bg-[#282a29] text-[#282A29] dark:text-[#f0f5f2] text-xs px-2 py-1 rounded border border-[#E8EBE9] dark:border-[#3d3d3d] focus:outline-none focus:border-bloom-green"
          >
            {filterConfig.options.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>
      ))}
    </div>
  );
};

// =============================================================================
// CHART RENDERING COMPONENT (with filters and interactivity)
// =============================================================================
const ChartRenderer = ({ chartData }) => {
  const { fontSize } = useGlobalFontSize();
  const [activeFilters, setActiveFilters] = useState({});

  // Extract data early for hooks (before any conditional returns)
  const data = chartData?.data || [];
  const full_data = chartData?.full_data;
  const sourceData = full_data || data;

  // Apply filters to data (useMemo must be called unconditionally)
  const filteredData = useMemo(() => {
    let result = sourceData;
    Object.entries(activeFilters).forEach(([field, value]) => {
      if (value && value !== 'All') {
        result = result.filter((item) => item[field] === value);
      }
    });
    return result;
  }, [sourceData, activeFilters]);

  // Early return after hooks
  if (!chartData || !data || data.length === 0) {
    return null;
  }

  // Support both 'type' and 'chart_type' field names (backend uses chart_type)
  const { type, chart_type, title, config, filters } = chartData;
  const chartType = type || chart_type;
  const description = config?.description;

  console.log('[ChartRenderer] Rendering chart type:', chartType, 'from fields:', { type, chart_type });
  console.log('[ChartRenderer] Config:', JSON.stringify(config));
  console.log('[ChartRenderer] Data sample (first 3):', JSON.stringify(data.slice(0, 3)));
  console.log('[ChartRenderer] series_field:', config?.series_field);
  console.log('[ChartRenderer] Unique series:', config?.series_field ? [...new Set(data.map(d => d[config.series_field]))] : 'none');

  // Handle map charts separately
  if (chartType === 'map') {
    return <MapChart chartData={chartData} />;
  }

  const xField = config?.x_axis || Object.keys(data[0])[0];
  const yField = config?.y_axis || Object.keys(data[0])[1];
  const seriesField = config?.series_field;

  // Format data for Recharts
  const formattedData = filteredData.map((item) => ({
    ...item,
    [xField]: item[xField]?.value
      ? new Date(item[xField].value).toLocaleDateString()
      : typeof item[xField] === 'string' && /^\d{4}-\d{2}/.test(item[xField])
        ? new Date(item[xField]).toLocaleDateString()
        : item[xField],
  }));

  // Get unique series if series_field is specified
  const series = seriesField
    ? [...new Set(filteredData.map((d) => d[seriesField]))]
    : [yField];

  // Handle filter change
  const handleFilterChange = (field, value) => {
    setActiveFilters((prev) => ({ ...prev, [field]: value }));
  };

  // Custom tooltip style
  const tooltipStyle = {
    contentStyle: {
      backgroundColor: '#1d1d1d',
      border: '1px solid #333',
      borderRadius: '8px',
      padding: '8px 12px',
    },
    labelStyle: { color: '#16A34A', fontWeight: 'bold' },
    itemStyle: { color: '#f0f5f2' },
  };

  const renderChart = () => {
    switch (chartType) {
      case 'bar':
        return (
          <BarChart data={formattedData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#333" />
            <XAxis dataKey={xField} tick={{ fill: '#888', fontSize: fontSize - 2 }} angle={-45} textAnchor="end" height={80} />
            <YAxis tick={{ fill: '#888', fontSize: fontSize - 2 }} domain={getSmartYDomain(formattedData, [yField])} allowDataOverflow={false} />
            <Tooltip {...tooltipStyle} />
            <Legend wrapperStyle={{ paddingTop: '10px' }} />
            {seriesField ? (
              series.map((s, i) => (
                <Bar key={s} dataKey={yField} name={s} fill={CHART_COLORS[i % CHART_COLORS.length]} />
              ))
            ) : (
              <Bar dataKey={yField} fill={config?.color || '#16A34A'} />
            )}
          </BarChart>
        );

      case 'area':
        return (
          <AreaChart data={formattedData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#333" />
            <XAxis dataKey={xField} tick={{ fill: '#888', fontSize: fontSize - 2 }} />
            <YAxis tick={{ fill: '#888', fontSize: fontSize - 2 }} domain={getSmartYDomain(formattedData, [yField])} allowDataOverflow={false} />
            <Tooltip {...tooltipStyle} />
            <Legend wrapperStyle={{ paddingTop: '10px' }} />
            {seriesField ? (
              series.map((s, i) => (
                <Area
                  key={s}
                  type="monotone"
                  dataKey={yField}
                  name={s}
                  stroke={CHART_COLORS[i % CHART_COLORS.length]}
                  fill={CHART_COLORS[i % CHART_COLORS.length] + '33'}
                />
              ))
            ) : (
              <Area type="monotone" dataKey={yField} stroke={config?.color || '#16A34A'} fill={(config?.color || '#16A34A') + '33'} />
            )}
          </AreaChart>
        );

      case 'pie':
        return (
          <PieChart>
            <Pie
              data={formattedData}
              dataKey={yField}
              nameKey={xField}
              cx="50%"
              cy="50%"
              outerRadius={100}
              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
            >
              {formattedData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip {...tooltipStyle} />
            <Legend wrapperStyle={{ paddingTop: '10px' }} />
          </PieChart>
        );

      case 'scatter':
        return (
          <ScatterChart>
            <CartesianGrid strokeDasharray="3 3" stroke="#333" />
            <XAxis dataKey={xField} tick={{ fill: '#888', fontSize: fontSize - 2 }} name={xField} />
            <YAxis dataKey={yField} tick={{ fill: '#888', fontSize: fontSize - 2 }} name={yField} />
            <ZAxis range={[60, 400]} />
            <Tooltip
              cursor={{ strokeDasharray: '3 3' }}
              {...tooltipStyle}
            />
            <Legend wrapperStyle={{ paddingTop: '10px' }} />
            {seriesField ? (
              series.map((s, i) => (
                <Scatter
                  key={s}
                  name={s}
                  data={formattedData.filter((d) => d[seriesField] === s)}
                  fill={CHART_COLORS[i % CHART_COLORS.length]}
                />
              ))
            ) : (
              <Scatter name={yField} data={formattedData} fill={config?.color || '#16A34A'} />
            )}
          </ScatterChart>
        );

      case 'line':
      default:
        return (
          <LineChart data={formattedData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#333" />
            <XAxis dataKey={xField} tick={{ fill: '#888', fontSize: fontSize - 2 }} />
            <YAxis tick={{ fill: '#888', fontSize: fontSize - 2 }} domain={getSmartYDomain(formattedData, [yField])} allowDataOverflow={false} />
            <Tooltip {...tooltipStyle} />
            <Legend wrapperStyle={{ paddingTop: '10px' }} />
            {seriesField ? (
              series.map((s, i) => (
                <Line
                  key={s}
                  type="monotone"
                  dataKey={yField}
                  name={s}
                  stroke={CHART_COLORS[i % CHART_COLORS.length]}
                  dot={{ fill: CHART_COLORS[i % CHART_COLORS.length], r: 3 }}
                  activeDot={{ r: 6, strokeWidth: 2 }}
                />
              ))
            ) : (
              <Line
                type="monotone"
                dataKey={yField}
                stroke={config?.color || '#16A34A'}
                dot={{ fill: config?.color || '#16A34A', r: 3 }}
                activeDot={{ r: 6, strokeWidth: 2 }}
              />
            )}
          </LineChart>
        );
    }
  };

  return (
    <div className="mt-4 p-4 bg-white dark:bg-[#1d1d1d] rounded-lg">
      {/* Title and subtitle */}
      {title && <h3 className="text-bloom-green font-semibold mb-1 text-base">{title}</h3>}
      {description && <p className="text-[#828382] dark:text-[#888] text-sm mb-3">{description}</p>}

      {/* Filters */}
      <ChartFilters
        filters={filters}
        activeFilters={activeFilters}
        onFilterChange={handleFilterChange}
      />

      {/* Chart */}
      <ResponsiveContainer width="100%" height={300}>
        {renderChart()}
      </ResponsiveContainer>

      {/* Data count indicator */}
      {filters && Object.keys(filters).length > 0 && (
        <p className="text-[#828382] dark:text-[#666] text-xs mt-2">
          Showing {formattedData.length} of {sourceData.length} data points
        </p>
      )}
    </div>
  );
};

// useState-style setters for one chat's live entry. A streaming turn binds them
// to its own chat id so late callbacks never write into the chat on screen.
const createChatStateSetters = (live, chatId) => ({
  setMessages: (value) => live.set(chatId, 'messages', value),
  setIsStreaming: (value) => live.set(chatId, 'isStreaming', value),
  setStatusMessage: (value) => live.set(chatId, 'statusMessage', value),
  setPdfDownload: (value) => live.set(chatId, 'pdfDownload', value),
  setPdfDownloadMessageId: (value) => live.set(chatId, 'pdfDownloadMessageId', value),
  setContextLevel: (value) => live.set(chatId, 'contextLevel', value),
  setContextFullInfo: (value) => live.set(chatId, 'contextFullInfo', value),
  setIsGeneratingReport: (value) => live.set(chatId, 'isGeneratingReport', value),
});

const biqHistorySignature = (conversationId, messages, isStreaming) =>
  `${conversationId}:${messages.length}:${isStreaming ? 1 : 0}`;

const BiQ = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { pageContext } = useBiqPageContext() || {};
  const { email } = useUser();
  const { fontSize } = useGlobalFontSize();
  const { isDark } = useTheme();
  // Manual entry point into the onboarding tour (the tour also auto-opens once
  // per new user — see TutorialContext). BiQ is the post-login landing page, so
  // the CTA lives here and nowhere else in the app.
  const { startTutorial } = useTutorial();
  const [inputValue, setInputValue] = useState('');
  const [pendingFile, setPendingFile] = useState(null);
  const [attachError, setAttachError] = useState('');
  const [loading, setLoading] = useState(false);
  // Context guardrails (persistent-sessions plan, Phase 3 section A): the
  // active session's admission level and the hard-stop panel state. `null`
  // means "no guardrail signal yet for this session" (short session, or not
  // loaded) - distinct from 'ok', which is an explicit signal from the
  // backend that this session HAS been classified and is fine.
  const [warningDismissed, setWarningDismissed] = useState(false);
  const {
    activeChatId,
    chats,
    startNewChat,
    getChatMessages,
    applyLocalTitlePlaceholder,
    applyServerTitle,
    replaceChatId,
  } = useBiqChatHistory();
  // Per-chat live state (messages, streaming flags, abort handles, PDF, context
  // guardrails). It lives in a provider above this page and is keyed by
  // conversation id, so a turn keeps generating while another chat is on
  // screen - this component only renders the active chat's entry.
  const live = useBiqLiveChats();
  const {
    messages,
    isStreaming,
    statusMessage,
    pdfDownload,
    pdfDownloadMessageId,
    contextLevel,
    contextFullInfo,
    isGeneratingReport,
  } = useLiveChat(activeChatId);
  // Setters bound to the chat on screen. A turn uses its own (see `beginTurn`).
  const {
    setMessages,
    setIsStreaming,
    setStatusMessage,
    setPdfDownloadMessageId,
    setContextFullInfo,
    setIsGeneratingReport,
  } = useMemo(() => createChatStateSetters(live, activeChatId), [live, activeChatId]);
  const conversationIdRef = useRef(activeChatId);
  // Thread currently rendered. Kept in step with `activeChatId` so the sidebar
  // can drive us, while a backend-issued id (below) doesn't read as a switch.
  const displayedChatIdRef = useRef(null);
  const historySignatureRef = useRef('');

  // `fromId` is the chat a turn started in. It matters when the user has since
  // switched to another chat: the backend re-keying a background turn must
  // re-key that turn's thread, not whatever is on screen now.
  const syncConversationId = useCallback(
    (id, fromId) => {
      if (!id) return;
      const previousId = live.resolve(fromId || conversationIdRef.current);
      if (previousId === id) return;
      if (live.resolve(conversationIdRef.current) === previousId) {
        conversationIdRef.current = id;
        displayedChatIdRef.current = id;
      }
      // Re-keys the sidebar row and the live entry instead of leaving duplicates.
      replaceChatId(previousId, id);
    },
    [live, replaceChatId]
  );
  const [showDataReportsDialog, setShowDataReportsDialog] = useState(false);
  const [deepThinking, setDeepThinking] = useState(location.state?.deepThinking || false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareModalUrl, setShareModalUrl] = useState('');
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const [copiedUserMessageId, setCopiedUserMessageId] = useState(null);
  // Desktop reveals the per-message action rows on hover; touch has no hover,
  // so the question's actions move into a long-press menu instead.
  const hasHover = useHasHover();
  const [questionMenu, setQuestionMenu] = useState(null);
  const longPressRef = useRef({ timer: null, x: 0, y: 0 });
  useEffect(() => () => clearTimeout(longPressRef.current.timer), []);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  // Auto-scroll only follows the stream while the user is parked at the bottom.
  // Scrolling up hands control back to them until they come back down.
  const autoScrollRef = useRef(true);
  const lastScrollTopRef = useRef(0);
  // Mirrors `chats` for the context-status lookup in the activeChatId-switch
  // effect below, so that effect doesn't need `chats` in its dependency
  // array (it would otherwise re-run - and re-fetch - on every sidebar
  // title update, not just on an actual thread switch).
  const chatsRef = useRef(chats);
  chatsRef.current = chats;
  // Composer draft carried from a context_full "Start new chat" click across
  // the thread-switch effect's normal setInputValue('') reset - see
  // handleStartNewChatFromContextFull and the effect below.
  const pendingDraftRef = useRef(null);
  const pendingAttachRef = useRef(null);
  const welcomeInputRef = useRef(null);
  const chatInputRef = useRef(null);

  const { 
    trackUsage: trackBiqUsage, 
    isExceeded: isBiqExceeded, 
    usage: biqUsage,
    loading: biqUsageLoading,
    lastReset: biqLastReset,
  } = useUsageTracking(USAGE_FEATURES.BIQ_QUERY);

  const { 
    trackUsage: trackDeepThinkingUsage, 
    isExceeded: isDeepThinkingExceeded, 
    usage: deepThinkingUsage,
    lastReset: deepThinkingLastReset,
  } = useUsageTracking(USAGE_FEATURES.BIQ_DEEP_THINKING);

  // Note: REPORT_GENERATION tracking removed - one-click market trends are unlimited
  // Backend logs usage to BigQuery (bloom-monitoring.webapp.market_trends) for analytics

  // Sample hint cards data
  const hintCards = [
    {
      title: t('biq.marketAnalysis'),
      description: t('biq.marketAnalysisDesc'),
    },
    {
      title: t('biq.pricePredictions'),
      description: t('biq.pricePredictionsDesc'),
    },
    {
      title: t('biq.dataReports'),
      description: t('biq.dataReportsDesc'),
      isDataReport: true,
    },
  ];

  // Every streamed chunk pushes a new `messages` value, so this is what keeps the
  // view glued to the answer as it types out — but only while the user hasn't
  // scrolled away to read something further up.
  useEffect(() => {
    if (!autoScrollRef.current) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Programmatic scrolls only ever move downwards, so any upward movement is the
  // user's own. Returning to within a few pixels of the bottom re-arms following.
  const handleMessagesScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const scrolledUp = el.scrollTop < lastScrollTopRef.current - 1;
    lastScrollTopRef.current = el.scrollTop;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= 48;
    if (atBottom) autoScrollRef.current = true;
    else if (scrolledUp) autoScrollRef.current = false;
  }, []);

  // Restore requests race the user clicking to another thread before the
  // fetch resolves; only the most recent request may commit its result.
  const restoreRequestIdRef = useRef(0);

  useEffect(() => {
    if (!activeChatId || displayedChatIdRef.current === activeChatId) return;

    displayedChatIdRef.current = activeChatId;

    // Switching threads never stops a generation: a running turn keeps
    // streaming into its own live entry and is shown again when the user
    // returns. Only chats that are finished (the backend has them) are
    // dropped from memory.
    live.evictIdle(activeChatId);
    live.ensure(activeChatId);
    const existing = live.get(activeChatId);
    const hasLiveMessages = existing.messages.length > 0;

    conversationIdRef.current = activeChatId;
    historySignatureRef.current = biqHistorySignature(
      activeChatId,
      existing.messages,
      existing.isStreaming
    );
    autoScrollRef.current = true;
    lastScrollTopRef.current = 0;

    // Normally a thread switch clears the composer - but a context_full
    // "Start new chat" click stashes the in-flight draft here first so it
    // survives into the fresh thread instead of being lost to this reset.
    const carriedDraft = pendingDraftRef.current;
    pendingDraftRef.current = null;
    const carriedFile = pendingAttachRef.current;
    pendingAttachRef.current = null;
    setInputValue(carriedDraft ?? '');
    setPendingFile(carriedFile);
    setAttachError('');
    setEditingMessageId(null);
    setEditDraft('');
    setWarningDismissed(false);

    live.set(activeChatId, 'unread', false);

    if (hasLiveMessages) {
      // A turn is (or was) running here: its live messages are the source of
      // truth. Refetching would drop the still-streaming answer, since the
      // backend only stores the assistant reply once it is complete — and
      // would also drop live-only extras (chartData, PDF ids).
      restoreRequestIdRef.current += 1; // invalidate any restore still in flight
      setLoading(false);
      return;
    }

    // Show the persisted guardrail level (if the backend's conversation list
    // included one - see BiqChatHistoryContext.toSidebarChat) immediately on
    // reload/resume, before this thread's next turn produces a fresh
    // `context_status` SSE event.
    const persistedChat = chatsRef.current.find((c) => c.id === activeChatId);
    live.set(activeChatId, 'contextLevel', persistedChat?.contextStatus || null);

    // Messages now live on the backend - fetch async. A brand-new, never-sent
    // chat id has no session doc, so there is nothing to restore: take the
    // loading state out of the picture entirely rather than setting it and
    // clearing it a microtask later, which flashed the restore spinner over
    // the welcome screen on every open of a fresh thread (getChatMessages
    // skips the round trip for this case too).
    if (!persistedChat) {
      restoreRequestIdRef.current += 1; // invalidate any restore still in flight
      setLoading(false);
      return;
    }

    setLoading(true);
    const requestId = ++restoreRequestIdRef.current;
    getChatMessages(activeChatId).then((restored) => {
      if (restoreRequestIdRef.current !== requestId) return; // superseded by a newer switch
      const current = live.get(activeChatId);
      // The user may have already sent a message here while this loaded.
      if (current.messages.length === 0 && !current.isStreaming && !current.isSending) {
        live.set(activeChatId, 'messages', restored);
        historySignatureRef.current = biqHistorySignature(activeChatId, restored, false);
      }
      setLoading(false);
    });
  }, [activeChatId, live, getChatMessages]);

  // Show an immediate local placeholder title for a new/active thread until
  // the backend's real title arrives (see applyLocalTitlePlaceholder).
  useEffect(() => {
    if (!activeChatId || messages.length === 0) return;
    const signature = biqHistorySignature(activeChatId, messages, isStreaming);
    if (signature === historySignatureRef.current) return;
    historySignatureRef.current = signature;
    applyLocalTitlePlaceholder(activeChatId, messages);
  }, [messages, isStreaming, activeChatId, applyLocalTitlePlaceholder]);

  // Re-hydrate PDF metadata from backend once per conversation. Do not depend on `messages`:
  // streaming updates `messages` constantly and would spam GET /report/pdf/latest (often 404 when
  // the backend only signals PDFs via `pdf_ready` + POST /report/pdf).
  useEffect(() => {
    if (!activeChatId) return;
    // never-sent chat has no session doc and so cannot have a
    // report behind it - probing would just 404.
    if (!chatsRef.current.some((c) => c.id === activeChatId)) return;
    let cancelled = false;
    (async () => {
      try {
        const latest = await getLatestPdfReport(activeChatId);
        if (cancelled) return;
        if (latest?.pdf_id) {
          live.set(activeChatId, 'pdfDownload', { pdf_id: latest.pdf_id, filename: latest.filename });
          const msgs = live.get(activeChatId).messages || [];
          live.set(activeChatId, 'pdfDownloadMessageId', (prev) => {
            if (prev) return prev;
            for (let i = msgs.length - 1; i >= 0; i--) {
              if (msgs[i]?.type === 'biq') return msgs[i].id;
            }
            return null;
          });
        }
        // 404 / empty: leave state alone so `pdf_ready` + download still work when /latest isn't implemented.
      } catch (e) {
        if (!cancelled) console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeChatId, live]);

  // If backend returned PDF metadata before messages finished loading, attach to the last BiQ bubble.
  useEffect(() => {
    if (pdfDownloadMessageId != null) return;
    const hasPdf =
      pdfDownload?.pdf_id ||
      pdfDownload?.payload?.pdf_id ||
      pdfDownload?.endpoint;
    if (!hasPdf) return;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i]?.type === 'biq') {
        setPdfDownloadMessageId(messages[i].id);
        break;
      }
    }
  }, [messages, pdfDownload, pdfDownloadMessageId, setPdfDownloadMessageId]);

  // Handle shared question from query string (?q=...)
  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const sharedQuestion = params.get('q');
    if (!sharedQuestion) return;

    // Clear the query param so refresh/back doesn't re-trigger
    window.history.replaceState({}, document.title, location.pathname);

    setInputValue(sharedQuestion);
    setTimeout(() => handleSendMessage(sharedQuestion), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildShareUrl = (question) => {
    const websiteBase = import.meta.env.VITE_WEBSITE_URL || window.location.origin;
    const cleanWebsiteBase = typeof websiteBase === 'string' ? websiteBase.replace(/\/+$/, '') : window.location.origin;
    const baseUrl = `${cleanWebsiteBase}/`;
    const qs = new URLSearchParams({ q: question });
    return `${baseUrl}?${qs.toString()}`;
  };

  const handleShareQuestion = (question, chatId) => {
    setShareModalUrl(buildShareUrl(question));
    setShareModalOpen(true);
    if (chatId) {
      sendBiqResponseFeedback(chatId, 'share').catch((e) => {
        console.error('BiQ share tracking failed:', e);
      });
    }
  };

  // Handle navigation from Home page with generateReport state
  const pendingReportRef = useRef(location.state?.generateReport ? location.state.commodity : null);

  useEffect(() => {
    if (pendingReportRef.current) {
      const commodity = pendingReportRef.current;
      pendingReportRef.current = null;

      // Clear the navigation state
      window.history.replaceState({}, document.title);

      // Small delay to ensure component is fully mounted
      setTimeout(() => {
        handleGenerateReport(commodity);
      }, 100);
    }
  }, []);

  const handlePickFile = (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      setAttachError(t('biq.fileTooLarge'));
      return;
    }
    setAttachError('');
    setPendingFile(file);
  };

  // Claims the chat on screen for a new turn. The returned handle is bound to
  // that chat, so callbacks that fire after the user switched to another chat
  // still write into the right thread. Null when the chat is busy or the
  // per-user concurrency cap is reached.
  const beginTurn = () => {
    const chatId = conversationIdRef.current;
    live.ensure(chatId);
    if (isBusy(live.get(chatId))) return null;
    if (live.countBusy(chatId) >= MAX_CONCURRENT_TURNS) {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          type: 'biq',
          content: t('biq.tooManyConcurrent'),
          timestamp: new Date(),
          isError: true,
        },
      ]);
      return null;
    }
    const isDisplayed = () => live.resolve(displayedChatIdRef.current) === live.resolve(chatId);
    return {
      chatId,
      ...createChatStateSetters(live, chatId),
      isDisplayed,
      markUnread: () => {
        if (!isDisplayed()) live.set(chatId, 'unread', true);
      },
    };
  };

  const handleSendMessage = async (overrideContent, sendOptions = {}) => {
    const {
      skipUserBubble = false,
      replaceBiqMessageId = null,
      editOfMessageId = null,
      regenerateOfMessageId = null,
      truncateFromMessageId = null,
    } = sendOptions;
    const fileToSend = overrideContent == null ? pendingFile : null;
    const userMessageContent = (overrideContent ?? inputValue).trim();
    if ((!userMessageContent && !fileToSend) || loading || isStreaming) {
      return;
    }

    const turn = beginTurn();
    if (!turn) return;
    const turnChatId = turn.chatId;

    // A fresh send always jumps back to the bottom, whatever the user was reading.
    autoScrollRef.current = true;
    // Snapshot for a context_full hard-stop (see the onContextFull callback
    // below): the turn is refused before any model call and nothing is
    // stored server-side, so the optimistic bubbles added below must be
    // fully unwound back to exactly this state — not just have their
    // content overwritten, the way a normal onError does.
    const messagesBeforeThisTurn = live.get(turnChatId).messages;

    // Synchronous limit checks using already-loaded usage data — block before any UI update
    if (deepThinking) {
      if (isDeepThinkingExceeded) {
        const errorMessage = {
          id: Date.now(),
          type: 'biq',
          content: `You've reached your limit for Deep Thinking queries. Your limit will reset soon. Please upgrade your subscription to continue using Deep Thinking without limits.`,
          timestamp: new Date(),
          isError: true,
        };
        turn.setMessages((prev) => [...prev, errorMessage]);
        return;
      }
    } else {
      if (!biqUsageLoading && isBiqExceeded) {
        const limitText = typeof biqUsage.limit === 'number' && biqUsage.limit > 0
          ? `your weekly limit of ${biqUsage.limit} BiQ queries`
          : 'your weekly BiQ query limit';
        const errorMessage = {
          id: Date.now(),
          type: 'biq',
          content: `You've reached ${limitText}. Your limit will reset soon. Please upgrade your subscription to continue using BiQ without limits.`,
          timestamp: new Date(),
          isError: true,
        };
        turn.setMessages((prev) => [...prev, errorMessage]);
        return;
      }
    }

    const abortController = new AbortController();
    live.patch(turnChatId, { isSending: true, abortController });

    let fileId = null;
    if (fileToSend) {
      turn.setIsStreaming(true);
      turn.setStatusMessage(t('biq.uploadingFile'));
      try {
        const uploaded = await uploadBiqUserFile(fileToSend, abortController.signal);
        fileId = uploaded?.file_id || null;
        if (!fileId) {
          throw new Error(t('biq.attachFailed'));
        }
      } catch (err) {
        live.patch(turnChatId, { isSending: false, abortController: null });
        turn.setIsStreaming(false);
        turn.setStatusMessage('');
        if (err?.name !== 'AbortError' && turn.isDisplayed()) {
          setAttachError(err?.message || t('biq.attachFailed'));
        }
        return;
      }
    }

    // Optimistic UI: show user message and BiQ placeholder immediately so the
    // chat never looks frozen while usage-tracking calls are in flight
    const userMessageLocalId = Date.now();
    const biqMessageId = replaceBiqMessageId ?? Date.now() + 1;

    if (replaceBiqMessageId) {
      if (live.get(turnChatId).pdfDownloadMessageId === replaceBiqMessageId) {
        turn.setPdfDownload(null);
        turn.setPdfDownloadMessageId(null);
      }
      turn.setMessages((prev) => {
        const idx = prev.findIndex((m) => m.id === replaceBiqMessageId);
        if (idx === -1) return prev;
        // Truncate anything after the regenerated assistant bubble
        return prev.slice(0, idx + 1).map((m) =>
          m.id === replaceBiqMessageId
            ? {
                ...m,
                content: '',
                isStreaming: true,
                chartData: undefined,
                chatId: null,
                userFeedback: null,
                firestoreMessageId: undefined,
              }
            : m
        );
      });
    } else {
      const userMessage = {
        id: userMessageLocalId,
        type: 'user',
        content: userMessageContent || (fileToSend ? fileToSend.name : ''),
        attachmentName: fileToSend ? fileToSend.name : undefined,
        timestamp: new Date(),
      };

      const biqMessage = {
        id: biqMessageId,
        type: 'biq',
        content: '',
        timestamp: new Date(),
        isStreaming: true,
        userPrompt: userMessageContent || (fileToSend ? fileToSend.name : ''),
        userMessageId: skipUserBubble ? undefined : userMessageLocalId,
      };

      turn.setMessages((prev) => {
        let base = prev;
        if (truncateFromMessageId != null) {
          const idx = prev.findIndex((m) => m.id === truncateFromMessageId);
          base = idx === -1 ? prev : prev.slice(0, idx);
        }
        return skipUserBubble ? [...base, biqMessage] : [...base, userMessage, biqMessage];
      });
    }

    if (turn.isDisplayed()) {
      setInputValue('');
      setPendingFile(null);
      setAttachError('');
      setEditingMessageId(null);
      setEditDraft('');
    }
    applyLocalTitlePlaceholder(turnChatId, live.get(turnChatId).messages);
    // Show "thinking" immediately (only until first token arrives)
    turn.setStatusMessage(t('biq.thinking'));
    turn.setIsStreaming(true);

    // Track chat message sent (skip duplicate count when regenerating in place)
    if (!replaceBiqMessageId) {
      trackChatMessageSent((userMessageContent || fileToSend?.name || '').length);
    }

    // Fire usage tracking in the background — don't await so the stream can start immediately
    if (deepThinking) {
      trackDeepThinkingUsage().then((trackResult) => {
        if (trackResult?.exceeded) {
          console.info('track result exceeded:', trackResult);
        }
      }).catch(() => {});
    } else {
      trackBiqUsage().catch(() => {});
    }

    try {

      let hasStartedAnswer = false;
      const agenticOptions = {
        conversationId: turnChatId,
        deepThinking,
        language: i18n.language,
        pageContext,
        onCancelToken: (tok) => {
          live.set(turnChatId, 'cancelToken', tok);
        },
        onConversationTitle: (event) => {
          applyServerTitle(event.conversation_id, event.title, event.title_source);
        },
        // Context guardrails (persistent-sessions plan, Phase 3 section A).
        onContextStatus: (event) => {
          const level = event?.level;
          if (level === 'ok' || level === 'warning' || level === 'urgent') {
            turn.setContextLevel(level);
          }
        },
        onContextFull: (event) => {
          // Hard stop: unwind this turn's optimistic bubbles entirely (the
          // message was never sent/stored) and put the draft back in the
          // composer rather than leaving it as a fake "sent" bubble.
          turn.setMessages(messagesBeforeThisTurn);
          turn.setIsStreaming(false);
          turn.setStatusMessage('');
          turn.setContextFullInfo({
            message: event?.message || t('biq.contextGuardrail.fullBody'),
          });
          if (turn.isDisplayed()) {
            setInputValue(userMessageContent);
            setPendingFile(fileToSend);
          }
        },
        // Never send both edit and regenerate on the same request
        ...(editOfMessageId && !regenerateOfMessageId
          ? { editOfMessageId }
          : {}),
        ...(regenerateOfMessageId && !editOfMessageId
          ? { regenerateOfMessageId }
          : {}),
        ...(fileId ? { fileId } : {}),
      };

      const chatResult = await sendBiqAgenticMessage(
        email,
        userMessageContent,
        agenticOptions,
        // onStatus - show status only before text starts streaming
        (event) => {
          if (event?.chat_id || event?.user_message_id || event?.assistant_message_id) {
            const userFs =
              event?.user_message_id ?? event?.userMessageId ?? null;
            const assistantFs =
              event?.assistant_message_id ?? event?.assistantMessageId ?? null;
            turn.setMessages((prev) => {
              const newMessages = [...prev];
              const msgIndex = newMessages.findIndex((m) => m.id === biqMessageId);
              if (msgIndex === -1) return prev;

              if (event?.chat_id && !newMessages[msgIndex].chatId) {
                newMessages[msgIndex] = {
                  ...newMessages[msgIndex],
                  chatId: event.chat_id,
                };
              }
              if (assistantFs && !newMessages[msgIndex].firestoreMessageId) {
                newMessages[msgIndex] = {
                  ...newMessages[msgIndex],
                  firestoreMessageId: assistantFs,
                };
              }
              if (userFs) {
                const userLocalId = newMessages[msgIndex].userMessageId;
                let userIdx = userLocalId != null
                  ? newMessages.findIndex((m) => m.id === userLocalId)
                  : -1;
                if (userIdx === -1) {
                  for (let i = msgIndex - 1; i >= 0; i -= 1) {
                    if (newMessages[i].type === 'user') {
                      userIdx = i;
                      break;
                    }
                  }
                }
                if (userIdx !== -1 && !newMessages[userIdx].firestoreMessageId) {
                  newMessages[userIdx] = {
                    ...newMessages[userIdx],
                    firestoreMessageId: userFs,
                  };
                }
              }
              return newMessages;
            });
          }
          if (hasStartedAnswer) return;
          const statusText = event?.message || event?.summary || '';
          turn.setStatusMessage(formatBiqStatusLineForDisplay(statusText));
        },
        // onTextDelta - stream text tokens
        // isReset=true means iteration changed (preamble should be replaced)
        (delta, isReset) => {
          if (!hasStartedAnswer) {
            hasStartedAnswer = true;
            turn.setStatusMessage('');
          }
          turn.setMessages((prev) => {
            const newMessages = [...prev];
            const msgIndex = newMessages.findIndex((m) => m.id === biqMessageId);
            if (msgIndex !== -1) {
              newMessages[msgIndex] = {
                ...newMessages[msgIndex],
                // If isReset, replace content entirely (discard preamble)
                // Otherwise, append delta to existing content
                content: isReset ? delta : newMessages[msgIndex].content + delta,
              };
            }
            return newMessages;
          });
        },
        // onComplete - mark streaming as done; persist Firestore message IDs when present
        (event) => {
          if (event?.conversation_id) {
            syncConversationId(event.conversation_id, turnChatId);
          }
          const userFirestoreId =
            event?.user_message_id ??
            event?.userMessageId ??
            event?.user_message?.id ??
            null;
          const assistantFirestoreId =
            event?.assistant_message_id ??
            event?.assistantMessageId ??
            event?.assistant_message?.id ??
            event?.message_id ??
            null;
          turn.setMessages((prev) => {
            const newMessages = [...prev];
            const msgIndex = newMessages.findIndex((m) => m.id === biqMessageId);
            if (msgIndex !== -1) {
              const streamed = newMessages[msgIndex].content;
              const nextContent = finalizeBiqAssistantContent(streamed, event);
              let linkedUserMessageId = newMessages[msgIndex].userMessageId;

              if (userFirestoreId) {
                let userIdx = linkedUserMessageId != null
                  ? newMessages.findIndex((m) => m.id === linkedUserMessageId)
                  : -1;
                if (userIdx === -1) {
                  for (let i = msgIndex - 1; i >= 0; i -= 1) {
                    if (newMessages[i].type === 'user') {
                      userIdx = i;
                      linkedUserMessageId = newMessages[i].id;
                      break;
                    }
                  }
                }
                if (userIdx !== -1) {
                  newMessages[userIdx] = {
                    ...newMessages[userIdx],
                    firestoreMessageId: userFirestoreId,
                  };
                }
              }

              newMessages[msgIndex] = {
                ...newMessages[msgIndex],
                content: nextContent,
                isStreaming: false,
                chatId: event?.chat_id || newMessages[msgIndex].chatId || null,
                firestoreMessageId:
                  assistantFirestoreId || newMessages[msgIndex].firestoreMessageId || null,
                userMessageId: linkedUserMessageId ?? newMessages[msgIndex].userMessageId,
                // Prefer complete event chart data if available, otherwise keep chart_ready data
                chartData: (event.render_chart && event.chart_data) ? event.chart_data : newMessages[msgIndex].chartData,
              };
            }
            return newMessages;
          });
          turn.setIsStreaming(false);
          turn.setStatusMessage('');
          turn.markUnread();
        },
        // onChartReady - render chart immediately when ready (parallel rendering)
        (chartData) => {
          console.log('[BiQ] chart_ready event received, rendering chart');
          turn.setMessages((prev) => {
            const newMessages = [...prev];
            const msgIndex = newMessages.findIndex((m) => m.id === biqMessageId);
            if (msgIndex !== -1) {
              newMessages[msgIndex] = {
                ...newMessages[msgIndex],
                chartData: chartData,
              };
            }
            return newMessages;
          });
        },
        // onPdfReady - show download button when report is ready
        (download) => {
          const cid = download?.payload?.conversation_id;
          if (cid) syncConversationId(cid, turnChatId);

          if (download?.pdf_id || download?.payload?.pdf_id || download?.endpoint) {
            turn.setPdfDownload(download);
            turn.setPdfDownloadMessageId(biqMessageId);
          }
        },
        // onError - handle errors gracefully
        (event) => {
          turn.setMessages((prev) => {
            const newMessages = [...prev];
            const msgIndex = newMessages.findIndex((m) => m.id === biqMessageId);
            if (msgIndex !== -1) {
              newMessages[msgIndex] = {
                ...newMessages[msgIndex],
                content: event.message || t('biq.errorProcessing'),
                isStreaming: false,
              };
            }
            return newMessages;
          });
          turn.setIsStreaming(false);
          turn.setStatusMessage('');
          turn.markUnread();
        },
        abortController.signal
      );
      if (chatResult?.conversationId) {
        syncConversationId(chatResult.conversationId, turnChatId);
      }
    } catch (error) {
      // Handle unexpected errors
      if (error.name !== 'AbortError') {
        console.error('BiQ error:', error);
        turn.setMessages((prev) => {
          const newMessages = [...prev];
          const msgIndex = newMessages.findIndex((m) => m.id === biqMessageId);
          if (msgIndex !== -1) {
            newMessages[msgIndex] = {
              ...newMessages[msgIndex],
              content: t('biq.errorGeneral'),
              isStreaming: false,
            };
          }
          return newMessages;
        });
        turn.markUnread();
      }
      turn.setIsStreaming(false);
      turn.setStatusMessage('');
    } finally {
      live.patch(turnChatId, {
        isSending: false,
        abortController: null,
        cancelToken: null,
      });
    }
  };

  // Handle key press in input
  const handleKeyDown = (e) => {
    // Enter sends; Shift+Enter inserts newline. Skip while IME is composing.
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Handle hint card click
  const handleHintClick = (hint) => {
    trackEvent(EVENTS.HINT_CARD_CLICKED, { hint_title: hint.title });
    if (hint.isDataReport) {
      setShowDataReportsDialog(true);
    } else {
      setInputValue(hint.description);
    }
  };

  const handleRegenerateResponse = (message) => {
    const prompt = message?.userPrompt?.trim();
    if (!prompt || loading || isStreaming) return;
    handleSendMessage(prompt, {
      replaceBiqMessageId: message.id,
      skipUserBubble: true,
      ...(message.firestoreMessageId
        ? { regenerateOfMessageId: message.firestoreMessageId }
        : {}),
    });
  };

  const handleCopyUserQuestion = async (message) => {
    const text = (message?.content || '').trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedUserMessageId(message.id);
      setTimeout(() => {
        setCopiedUserMessageId((prev) => (prev === message.id ? null : prev));
      }, 2000);
    } catch (e) {
      console.error('Copy failed:', e);
    }
  };

  const cancelQuestionLongPress = () => {
    clearTimeout(longPressRef.current.timer);
    longPressRef.current.timer = null;
  };

  const closeQuestionMenu = useCallback(() => setQuestionMenu(null), []);

  const openQuestionMenu = (message, x, y) => {
    cancelQuestionLongPress();
    setQuestionMenu({ messageId: message.id, x, y });
  };

  // Android/Chrome fire a native `contextmenu` after their own long-press
  // gesture, so prefer it -- the OS, not us, decides how long a press is.
  const handleQuestionContextMenu = (e, message) => {
    if (hasHover) return;
    e.preventDefault();
    // Both triggers can land on the same press; don't re-anchor an open menu.
    if (questionMenu?.messageId === message.id) return;
    openQuestionMenu(message, e.clientX, e.clientY);
  };

  // iOS Safari never fires `contextmenu` on plain text (it opens the selection
  // callout instead), so a timed press is the fallback trigger there.
  const handleQuestionPointerDown = (e, message) => {
    if (hasHover || e.pointerType === 'mouse') return;
    const { clientX, clientY } = e;
    cancelQuestionLongPress();
    longPressRef.current.x = clientX;
    longPressRef.current.y = clientY;
    longPressRef.current.timer = setTimeout(() => {
      openQuestionMenu(message, clientX, clientY);
    }, LONG_PRESS_MS);
  };

  // A drag means the user is scrolling the thread, not holding the bubble.
  const handleQuestionPointerMove = (e) => {
    const { timer, x, y } = longPressRef.current;
    if (!timer) return;
    if (Math.abs(e.clientX - x) > LONG_PRESS_SLOP_PX || Math.abs(e.clientY - y) > LONG_PRESS_SLOP_PX) {
      cancelQuestionLongPress();
    }
  };

  const handleStartEditUserMessage = (message) => {
    if (loading || isStreaming) return;
    setEditingMessageId(message.id);
    setEditDraft(message.content || '');
  };

  const handleCancelEditUserMessage = () => {
    setEditingMessageId(null);
    setEditDraft('');
  };

  const handleSaveEditUserMessage = (message) => {
    const newText = editDraft.trim();
    if (!newText || loading || isStreaming) return;
    handleSendMessage(newText, {
      truncateFromMessageId: message.id,
      ...(message.firestoreMessageId
        ? { editOfMessageId: message.firestoreMessageId }
        : {}),
    });
  };

  const handleMessageFeedbackChange = (messageId, feedback) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, userFeedback: feedback } : m))
    );
  };

  // Handle one-click market trends report generation with streaming
  // Note: Market trends are unlimited for all users - no usage limit check needed
  // Backend logs to BigQuery for analytics (bloom-monitoring.webapp.market_trends)
  const handleGenerateReport = async (commodity) => {
    trackEvent(EVENTS.MARKET_TRENDS_CLICK, { commodity });

    const turn = beginTurn();
    if (!turn) return;
    const turnChatId = turn.chatId;

    const marketTrendsPrompt = `Generate a comprehensive market trends report for ${commodity}. Include current prices, regional analysis, volume trends, and key market insights.`;
    const abortController = new AbortController();
    live.patch(turnChatId, { abortController, isGeneratingReport: true });
    if (turn.isDisplayed()) setShowDataReportsDialog(false);

    // Add system message showing the selected commodity
    const systemMessage = {
      id: Date.now(),
      type: 'system',
      content: t('biq.generatingReport', { commodity }),
      timestamp: new Date(),
    };
    turn.setMessages((prev) => [...prev, systemMessage]);

    // Add placeholder for streaming response
    const biqMessageId = Date.now() + 1;
    const biqMessage = {
      id: biqMessageId,
      type: 'biq',
      content: '',
      timestamp: new Date(),
      isStreaming: true,
      userPrompt: marketTrendsPrompt,
    };
    turn.setMessages((prev) => [...prev, biqMessage]);
    applyLocalTitlePlaceholder(turnChatId, live.get(turnChatId).messages);
    // No status text UI while streaming (cursor bar only once tokens arrive)
    turn.setStatusMessage('');
    turn.setIsStreaming(true);

    try {
      const chatResult = await sendBiqAgenticMessage(
        email,
        marketTrendsPrompt,
        {
          conversationId: turnChatId,
          marketTrends: true,
          language: i18n.language,
          onCancelToken: (token) => {
            live.set(turnChatId, 'cancelToken', token);
          },
          onConversationTitle: (event) => {
            applyServerTitle(event.conversation_id, event.title, event.title_source);
          },
        },
        // onStatus - ignored (we don't show status text in the UI while streaming)
        () => {},
        // onTextDelta - update the message with streamed content
        (delta, isReset) => {
          turn.setMessages((prev) => {
            const newMessages = [...prev];
            const lastMessage = newMessages[newMessages.length - 1];
            if (lastMessage && lastMessage.id === biqMessageId) {
              newMessages[newMessages.length - 1] = {
                ...lastMessage,
                content: isReset ? delta : lastMessage.content + delta,
              };
            }
            return newMessages;
          });
        },
        // onComplete - mark streaming as complete
        (event) => {
          if (event?.conversation_id) {
            syncConversationId(event.conversation_id, turnChatId);
          }
          trackEvent(EVENTS.MARKET_TRENDS_GENERATED, { commodity });
          turn.setMessages((prev) => {
            const newMessages = [...prev];
            const lastMessage = newMessages[newMessages.length - 1];
            if (lastMessage && lastMessage.id === biqMessageId) {
              const streamed = lastMessage.content;
              const nextContent = finalizeBiqAssistantContent(streamed, event);
              newMessages[newMessages.length - 1] = {
                ...lastMessage,
                content: nextContent,
                isStreaming: false,
                chatId: event?.chat_id || lastMessage.chatId || null,
              };
            }
            return newMessages;
          });
          turn.setIsStreaming(false);
          turn.setStatusMessage('');
          turn.markUnread();
        },
        // onChartReady - not used for market trends
        () => {},
        // onPdfReady - capture report download info
        (download) => {
          const cid = download?.payload?.conversation_id;
          if (cid) syncConversationId(cid, turnChatId);

          if (download?.pdf_id || download?.payload?.pdf_id || download?.endpoint) {
            turn.setPdfDownload(download);
            turn.setPdfDownloadMessageId(biqMessageId);
          }
        },
        // onError - handle error
        (event) => {
          trackEvent(EVENTS.MARKET_TRENDS_ERROR, { commodity, error: event.message });
          turn.setMessages((prev) => {
            const newMessages = [...prev];
            const lastMessage = newMessages[newMessages.length - 1];
            if (lastMessage && lastMessage.id === biqMessageId) {
              newMessages[newMessages.length - 1] = {
                ...lastMessage,
                content: event.message || t('biq.errorGenerating', { error: 'Unknown error' }),
                isStreaming: false,
              };
            }
            return newMessages;
          });
          turn.setIsStreaming(false);
          turn.setStatusMessage('');
          turn.markUnread();
        },
        abortController.signal
      );
      if (chatResult?.conversationId) {
        syncConversationId(chatResult.conversationId, turnChatId);
      }
    } catch (error) {
      console.error('Report generation error:', error);
    } finally {
      live.patch(turnChatId, {
        isGeneratingReport: false,
        abortController: null,
        cancelToken: null,
      });
    }
  };

  // Stop streaming
  const handleStopStreaming = () => {
    trackEvent(EVENTS.CHAT_STREAMING_STOPPED);
    live.abort(conversationIdRef.current);
    setLoading(false);
    setIsStreaming(false);
    setIsGeneratingReport(false);
    setStatusMessage('');

    // Keep any partial assistant message exactly as streamed.
    setMessages((prev) => {
      const newMessages = [...prev];
      const lastMessage = newMessages[newMessages.length - 1];
      if (lastMessage && lastMessage.isStreaming) {
        newMessages[newMessages.length - 1] = {
          ...lastMessage,
          isStreaming: false,
        };
      }
      return newMessages;
    });
  };

  // context_full panel's primary action: a fresh session, with the draft
  // that couldn't be sent on the old (too-long) one carried over. The actual
  // setInputValue(carried) happens in the activeChatId-switch effect above,
  // once the switch effect has run its own setInputValue('') reset — this
  // ref is how the draft survives that reset.
  const handleStartNewChatFromContextFull = () => {
    pendingDraftRef.current = inputValue || null;
    pendingAttachRef.current = pendingFile;
    setContextFullInfo(null);
    startNewChat();
  };

  const deepThinkingLimitExceeded = deepThinking && isDeepThinkingExceeded;
  const hasMessages = messages.length > 0;
  const brandLogoSrc = '/assets/companyname.svg';
  const assistantAvatarSrc = '/assets/logo.png';

  return (
    <UsageGuard
      feature={USAGE_FEATURES.BIQ_QUERY}
      featureName="BiQ"
      trackOnMount={false}
    >
      <div className="relative flex h-full min-h-0 w-full flex-col overflow-hidden">
        {createPortal(
          <div
            aria-hidden="true"
            className={`pointer-events-none fixed inset-0 -z-10 ${hasMessages ? 'biq-chat-bg' : 'biq-welcome-bg'}`}
          />,
          document.body
        )}
        {/* Deep Thinking limit overlay - shown when deep thinking mode is active and its limit is hit */}
        {deepThinkingLimitExceeded && (
          <UsageBlurOverlay
            feature={USAGE_FEATURES.BIQ_DEEP_THINKING}
            usage={deepThinkingUsage}
            lastReset={deepThinkingLastReset}
            featureName="Deep Thinking"
            onPrimaryAction={() => {
              setDeepThinking(false);
            }}
          />
        )}
        {/* Context guardrail hard stop (persistent-sessions plan, Phase 3
            section A): the turn was refused before any model call. Same
            blur-overlay treatment as the Deep Thinking limit above; the
            composer's draft is preserved (see onContextFull) and carried to
            the fresh thread by the primary button below. */}
        {contextFullInfo && (
          <div className="absolute inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-neutral-900/[0.12] backdrop-blur-[17.5px] dark:bg-[rgba(214,214,214,0.2)]"
              style={{ backdropFilter: 'blur(17.5px)', WebkitBackdropFilter: 'blur(17.5px)' }}
            />
            <div className="relative z-10 flex flex-col items-center gap-[16px] text-center max-w-full mx-12 sm:mx-16 md:mx-24 lg:mx-32">
              <AlertTriangle size={40} className="text-amber-500" />
              <p className="font-['Inter'] font-semibold text-[18px] text-[#282A29] dark:text-[#f0f5f2]">
                {t('biq.contextGuardrail.fullTitle')}
              </p>
              <p className="font-['Inter'] text-[14px] text-[#5D625F] dark:text-[#C3C4C3]">
                {contextFullInfo.message}
              </p>
              <button
                type="button"
                onClick={handleStartNewChatFromContextFull}
                className="bg-bloom-green hover:bg-bloom-green-hover text-white px-[24px] py-[12px] rounded-[8px] font-['Inter'] font-medium text-[16px] transition-colors"
              >
                {t('biq.contextGuardrail.startNewChat')}
              </button>
            </div>
          </div>
        )}
        <div className={(deepThinkingLimitExceeded || contextFullInfo) ? 'relative filter blur-sm pointer-events-none flex min-h-0 flex-1 flex-col' : 'relative flex min-h-0 flex-1 flex-col'}>
          <div className={`flex min-h-0 w-full flex-1 flex-col ${PAGE_X}`}>
            {/* Product-tour CTA. Kept in normal flow rather than absolutely
                positioned so it can't overlap the first chat message once a
                conversation starts.
                Temporarily hidden — re-enable when the tutorial ships. */}
            {/* <div className="flex shrink-0 justify-end pt-3 sm:pt-4">
              <button
                type="button"
                onClick={startTutorial}
                className="group inline-flex h-[36px] max-w-full items-center gap-[8px] rounded-[22px] border border-[#16A34A40] bg-[#16A34A14] pb-[10.5px] pl-[17px] pr-[17px] pt-[10.5px] text-[13.5px] font-semibold leading-none text-[#15803D] shadow-[0_0_0_1px_#16A34A,0_0_0_5px_#16A34A33] transition-colors hover:bg-[#16A34A24] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16A34A]/40 dark:border-[#16A34A66] dark:text-[#7EE6A4]"
              >
                <CirclePlay size={16} className="shrink-0" />
                <span className="truncate">{t('biq.tourCta')}</span>
                <ArrowRight size={16} className="shrink-0 transition-transform group-hover:translate-x-0.5" />
              </button>
            </div> */}

            {/* Messages / Welcome area */}
            <div
              ref={messagesContainerRef}
              onScroll={handleMessagesScroll}
              className={`min-h-0 flex-1 overflow-y-auto ${hasMessages ? 'py-2 sm:py-6' : 'flex flex-col'}`}
            >
              {!hasMessages && loading ? (
                // Restoring a conversation selected from the sidebar - fetched
                // messages haven't arrived yet. Distinct from the trailing
                // dot-bounce indicator below, which means "waiting for a
                // reply" mid-conversation and is untouched by this branch.
                <div
                  role="status"
                  aria-busy="true"
                  aria-label={t('biq.loadingConversation')}
                  className="flex h-full min-h-[360px] w-full flex-1 flex-col gap-4 p-4 md:p-8"
                >
                  <div className="skeleton-sheen h-16 w-2/3 max-w-xl rounded-[16px]" />
                  <div className="skeleton-sheen ml-auto h-12 w-1/2 max-w-md rounded-[16px]" />
                  <div className="skeleton-sheen h-20 w-3/5 max-w-lg rounded-[16px]" />
                  <div className="skeleton-sheen mt-auto h-12 w-full rounded-[24px]" />
                </div>
              ) : !hasMessages ? (
                <div className="mx-auto flex min-h-0 w-full max-w-4xl flex-1 flex-col items-center justify-center py-8">
                  <img
                    src={brandLogoSrc}
                    alt="Bloom IQ"
                    className="h-8 w-auto shrink-0 object-contain object-center sm:h-10 md:h-12"
                  />
                  <p className="mt-6 shrink-0 text-center text-[24px] font-medium text-[#282A29] dark:text-white">
                    {t('biq.title')}
                    {!t('biq.title').endsWith('.') && '.'}
                  </p>

                  <div className="mt-10 w-full shrink-0">
                    <BiqComposer
                      inputRef={welcomeInputRef}
                      inputValue={inputValue}
                      onInputChange={(e) => setInputValue(e.target.value)}
                      onKeyDown={handleKeyDown}
                      onFocus={() => setTimeout(() => welcomeInputRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }), 300)}
                      onSend={() => handleSendMessage()}
                      onStop={handleStopStreaming}
                      isStreaming={isStreaming}
                      loading={loading}
                      deepThinking={deepThinking}
                      onDeepThinkingChange={(enabled) => {
                        trackEvent(EVENTS.DEEP_THINKING_TOGGLED, { enabled });
                        setDeepThinking(enabled);
                      }}
                      pendingFile={pendingFile}
                      onPickFile={handlePickFile}
                      onClearFile={() => {
                        setPendingFile(null);
                        setAttachError('');
                      }}
                      attachError={attachError}
                      onPlusClick={() => startNewChat()}
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-4 sm:space-y-6">
                  {messages.map((message) => (
                    <div key={message.id} className={`group/msg flex gap-2 sm:gap-3 ${message.type === 'user' ? 'flex-row-reverse items-center' : ''}`}>
                      {message.type === 'system' ? (
                        <div className="w-full flex justify-center">
                          <div className="px-4 py-2 bg-white dark:bg-[#2a2a2a] border border-[#E8EBE9] dark:border-white/10 rounded-2xl">
                            <p className="text-[#828382] italic" style={{ fontSize }}>
                              {message.content}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <>
                          {message.type === 'biq' && (
                            <img
                              src={assistantAvatarSrc}
                              alt="BiQ"
                              className="w-7 h-7 sm:w-8 sm:h-8 object-contain flex-shrink-0 mt-1"
                            />
                          )}
                          {message.type === 'user' && (
                            <img
                              src="/assets/icons/user-avatar.png"
                              alt=""
                              width={32}
                              height={32}
                              className="w-7 h-7 sm:w-8 sm:h-8 rounded-full object-cover flex-shrink-0"
                            />
                          )}

                          <div className="flex-1 min-w-0">
                            {message.type === 'user' ? (
                              <div className="flex justify-end">
                                <div
                                  className={`relative flex max-w-[92%] flex-col gap-2 sm:max-w-[80%] ${
                                    editingMessageId === message.id ? 'w-full' : 'w-fit'
                                  }`}
                                >
                                  {editingMessageId === message.id ? (
                                    <div className="rounded-tl-[16px] rounded-tr-[16px] rounded-br-[4px] rounded-bl-[16px] border border-[#7EE6A4] bg-[#DCFCE7] dark:border-[#7EE6A4] dark:bg-[#1f3d28] px-4 py-3">
                                      <textarea
                                        value={editDraft}
                                        onChange={(e) => setEditDraft(e.target.value)}
                                        rows={3}
                                        className="composer-scroll w-full resize-none bg-transparent text-sm sm:text-base leading-relaxed text-[#282A29] dark:text-[#f0f5f2] focus:outline-none"
                                        autoFocus
                                      />
                                      <div className="mt-2 flex items-center justify-end gap-2">
                                        <button
                                          type="button"
                                          onClick={handleCancelEditUserMessage}
                                          className="px-3 py-1.5 text-xs sm:text-sm text-[#5D625F] dark:text-[#C3C4C3] hover:text-[#282A29] dark:hover:text-white transition-colors"
                                        >
                                          {t('biq.cancelEdit')}
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleSaveEditUserMessage(message)}
                                          disabled={!editDraft.trim() || loading || isStreaming}
                                          className="px-3 py-1.5 rounded-full bg-bloom-green text-white text-xs sm:text-sm font-medium hover:bg-bloom-green-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                          {t('biq.saveEdit')}
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                    <div
                                      className={`w-fit max-w-full rounded-tl-[16px] rounded-tr-[16px] rounded-br-[4px] rounded-bl-[16px] border border-[#7EE6A4] bg-[#DCFCE7] dark:border-[#7EE6A4] dark:bg-[#1f3d28] px-4 py-3 ${
                                        hasHover ? '' : 'select-none [-webkit-touch-callout:none]'
                                      }`}
                                      onPointerDown={(e) => handleQuestionPointerDown(e, message)}
                                      onPointerMove={handleQuestionPointerMove}
                                      onPointerUp={cancelQuestionLongPress}
                                      onPointerCancel={cancelQuestionLongPress}
                                      onContextMenu={(e) => handleQuestionContextMenu(e, message)}
                                    >
                                      {message.attachmentName ? (
                                        <div className={message.content && message.content !== message.attachmentName ? 'mb-2' : undefined}>
                                          <BiqAttachedFileChip
                                            name={message.attachmentName}
                                            label={t('biq.attachedFile')}
                                          />
                                        </div>
                                      ) : null}
                                      {message.content && message.content !== message.attachmentName ? (
                                      <p className="text-sm sm:text-base leading-relaxed text-left text-[#282A29] dark:text-[#f0f5f2] break-words whitespace-pre-wrap">
                                        {message.content}
                                      </p>
                                      ) : null}
                                    </div>
                                    {/* Below the bubble, not inside it: a hidden row inside the
                                        bubble padded it out and left it looking oddly tall while
                                        the actions were invisible. */}
                                    {hasHover ? (
                                      <div className="-mt-1 flex items-center justify-end gap-0.5 invisible opacity-0 pointer-events-none transition-opacity duration-150 group-hover/msg:visible group-hover/msg:opacity-100 group-hover/msg:pointer-events-auto group-focus-within/msg:visible group-focus-within/msg:opacity-100 group-focus-within/msg:pointer-events-auto">
                                        <button
                                          type="button"
                                          onClick={() => handleCopyUserQuestion(message)}
                                          className="p-1.5 rounded-md text-[#828382] hover:text-[#282A29] dark:hover:text-[#f0f5f2] hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
                                          aria-label={t('biq.copyQuestion')}
                                          title={t('biq.copyQuestion')}
                                        >
                                          {copiedUserMessageId === message.id ? (
                                            <Check size={16} className="text-bloom-green" />
                                          ) : (
                                            <Copy size={16} />
                                          )}
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleShareQuestion(
                                              message.content,
                                              findBiqChatIdForUserMessage(messages, message.id)
                                            )
                                          }
                                          className="p-1.5 rounded-md text-[#828382] hover:text-[#282A29] dark:hover:text-[#f0f5f2] hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
                                          aria-label={t('biq.shareQuestion')}
                                          title={t('biq.shareQuestion')}
                                        >
                                          <Share2 size={16} />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleStartEditUserMessage(message)}
                                          disabled={loading || isStreaming}
                                          className="p-1.5 rounded-md text-[#828382] hover:text-[#282A29] dark:hover:text-[#f0f5f2] hover:bg-black/5 dark:hover:bg-white/10 transition-colors disabled:opacity-40 disabled:pointer-events-none"
                                          aria-label={t('biq.editQuestion')}
                                          title={t('biq.editQuestion')}
                                        >
                                          <Pencil size={16} />
                                        </button>
                                      </div>
                                    ) : null}
                                    </>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div className="text-sm sm:text-base leading-relaxed text-[#282A29] dark:text-white">
                                {message.isStreaming && !message.content ? (
                                  <div className="flex items-center gap-3 pt-2">
                                    <div className="flex gap-1.5">
                                      <span className="w-2 h-2 bg-bloom-green rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                      <span className="w-2 h-2 bg-bloom-green rounded-full animate-bounce" style={{ animationDelay: '200ms' }} />
                                      <span className="w-2 h-2 bg-bloom-green rounded-full animate-bounce" style={{ animationDelay: '400ms' }} />
                                    </div>
                                    {statusMessage ? (
                                      <span className="text-sm sm:text-base leading-relaxed text-bloom-green">{statusMessage}</span>
                                    ) : null}
                                  </div>
                                ) : (
                                  <>
                                    {message.content && (
                                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                                        {message.content}
                                      </ReactMarkdown>
                                    )}

                                    {!message.isStreaming && message.content && !message.isError && (
                                      <BiqResponseActions
                                        chatId={message.chatId}
                                        content={message.content}
                                        userFeedback={message.userFeedback}
                                        onUserFeedbackChange={(feedback) =>
                                          handleMessageFeedbackChange(message.id, feedback)
                                        }
                                        onRegenerate={() => handleRegenerateResponse(message)}
                                        isStreaming={message.isStreaming}
                                        revealOnHover={hasHover}
                                      />
                                    )}

                                    {/* While streaming:
                                        - Before any text arrives: show status + 3 dots (above)
                                        - After text starts: show only the flickering bar under the answer
                                    */}
                                    {message.isStreaming && message.content ? (
                                      <div className="pt-2 mt-2">
                                        <span className="inline-block w-2 h-5 bg-bloom-green animate-pulse align-text-bottom" />
                                      </div>
                                    ) : null}

                                    {message.type === 'biq' && pdfDownload && pdfDownloadMessageId === message.id && (
                                      <div className="flex items-center justify-start mt-3">
                                        <button
                                          type="button"
                                          onClick={async () => {
                                            try {
                                              await downloadPdfReport(pdfDownload);
                                            } catch (e) {
                                              console.error(e);
                                            }
                                          }}
                                          className="bg-bloom-green text-white px-4 py-2 rounded-full hover:bg-bloom-green-hover transition-colors inline-flex items-center gap-2 text-sm"
                                        >
                                          <Download size={16} className="text-white" />
                                          Download PDF
                                        </button>
                                      </div>
                                    )}
                                  </>
                                )}

                                {/* Charts */}
                                {message.chartData && (message.chartData.chart_type === 'map' || message.chartData.type === 'map') && (
                                  <div className="w-full mt-2">
                                    <MapChart chartData={message.chartData} />
                                  </div>
                                )}
                                {message.chartData && (message.chartData.chart_type !== 'map' && message.chartData.type !== 'map') && (
                                  <div className="w-full mt-2">
                                    <ChartErrorBoundary>
                                      <Suspense fallback={<div className="text-[#828382] p-4">Loading chart...</div>}>
                                        <PlotlyChart chartData={message.chartData} />
                                      </Suspense>
                                    </ChartErrorBoundary>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  ))}

                  {/* Legacy loading indicator (kept for safety; streaming usually drives UI) */}
                  {loading && (
                    <div className="flex gap-2 sm:gap-3 items-start">
                      <img
                        src={assistantAvatarSrc}
                        alt="BiQ"
                        className="w-7 h-7 sm:w-8 sm:h-8 object-contain flex-shrink-0 mt-1"
                      />
                      <div className="pt-2">
                        <div className="flex gap-1.5">
                          <span className="w-2 h-2 bg-bloom-green rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-2 h-2 bg-bloom-green rounded-full animate-bounce" style={{ animationDelay: '200ms' }} />
                          <span className="w-2 h-2 bg-bloom-green rounded-full animate-bounce" style={{ animationDelay: '400ms' }} />
                        </div>
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {/* Bottom composer (only mounted in conversation view to avoid a duplicate hidden textarea) */}
            {hasMessages && (
            <div className="shrink-0 pt-1 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
              {/* Context guardrail banners (persistent-sessions plan, Phase 3
                  section A). 'urgent' always wins over a dismissed/pending
                  'warning' — the two levels never render together. */}
              {contextLevel === 'warning' && !warningDismissed && (
                <div className="mb-2 flex items-start gap-2 rounded-[8px] bg-amber-50 dark:bg-[#3d3a20] px-3 py-2">
                  <AlertTriangle size={16} className="text-amber-600 dark:text-[#f5a623] flex-shrink-0 mt-0.5" />
                  <p className="flex-1 font-['Inter'] text-[13px] leading-[18px] text-amber-800 dark:text-[#f5a623]">
                    {t('biq.contextGuardrail.warning')}
                  </p>
                  <button
                    type="button"
                    onClick={() => setWarningDismissed(true)}
                    aria-label={t('biq.contextGuardrail.dismiss')}
                    className="flex-shrink-0 text-amber-600 dark:text-[#f5a623] hover:opacity-70 transition-opacity"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}
              {contextLevel === 'urgent' && (
                <div className="mb-2 flex items-center gap-2 rounded-[8px] bg-red-50 dark:bg-[#3d2020] px-3 py-2">
                  <AlertTriangle size={16} className="text-red-600 dark:text-red-400 flex-shrink-0" />
                  <p className="flex-1 font-['Inter'] text-[13px] leading-[18px] text-red-800 dark:text-red-300">
                    {t('biq.contextGuardrail.urgent')}
                  </p>
                  <button
                    type="button"
                    onClick={() => startNewChat()}
                    className="flex-shrink-0 rounded-full bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-3 py-1.5 transition-colors"
                  >
                    {t('biq.contextGuardrail.newChat')}
                  </button>
                </div>
              )}
              <BiqComposer
                inputRef={chatInputRef}
                inputValue={inputValue}
                onInputChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() => setTimeout(() => chatInputRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }), 300)}
                onSend={() => handleSendMessage()}
                onStop={handleStopStreaming}
                isStreaming={isStreaming}
                loading={loading}
                deepThinking={deepThinking}
                onDeepThinkingChange={(enabled) => {
                  trackEvent(EVENTS.DEEP_THINKING_TOGGLED, { enabled });
                  setDeepThinking(enabled);
                }}
                onPlusClick={() => startNewChat()}
                conversationActive
                pendingFile={pendingFile}
                onPickFile={handlePickFile}
                onClearFile={() => {
                  setPendingFile(null);
                  setAttachError('');
                }}
                attachError={attachError}
              />

              <p className="text-xs text-[#828382] text-center mt-3">
                {t('biq.poweredBy')}
              </p>
            </div>
            )}
          </div>
        </div>

        {/* Data Reports Dialog */}
        <DataReportsDialog
          isOpen={showDataReportsDialog}
          onClose={() => setShowDataReportsDialog(false)}
          onConfirm={handleGenerateReport}
          isLoading={isGeneratingReport}
        />
        <ShareModal
          isOpen={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          shareUrl={shareModalUrl}
        />
        {questionMenu ? (() => {
          const message = messages.find((m) => m.id === questionMenu.messageId);
          if (!message) return null;
          return (
            <BiqLongPressMenu
              x={questionMenu.x}
              y={questionMenu.y}
              onClose={closeQuestionMenu}
              items={[
                {
                  key: 'copy',
                  label: t('biq.copyQuestion'),
                  icon: <Copy size={18} />,
                  onSelect: () => handleCopyUserQuestion(message),
                },
                {
                  key: 'share',
                  label: t('biq.shareQuestion'),
                  icon: <Share2 size={18} />,
                  onSelect: () =>
                    handleShareQuestion(
                      message.content,
                      findBiqChatIdForUserMessage(messages, message.id)
                    ),
                },
                {
                  key: 'edit',
                  label: t('biq.editQuestion'),
                  icon: <Pencil size={18} />,
                  disabled: loading || isStreaming,
                  onSelect: () => handleStartEditUserMessage(message),
                },
              ]}
            />
          );
        })() : null}
      </div>
    </UsageGuard>
  );
};

export default BiQ;
